import { BorderStyle } from 'modules/common/components/Border';
import { nanoid } from 'nanoid';

import { ShapeProps } from './Shapes.types';

interface SVGStrokeDashStyle {
  strokeLinecap?: 'round' | 'butt' | 'square';
  strokeDasharray?: string;
}

/**
 * Generates object with SVG stroke attributes given a border style
 * to mimic CSS 'dotted', 'dashed', 'solid', etc. border styling.
 *
 * @param borderStyle {BorderStyle} style of the border
 * @param borderWidth {number} width of border in px
 * @returns {SVGStrokeDashStyle}
 */
export const generateStrokeFromBorderStyle = (borderStyle: BorderStyle, borderWidth: number): SVGStrokeDashStyle => {
  const strokeDashStyle: SVGStrokeDashStyle = {};
  switch (borderStyle) {
    case BorderStyle.Dotted:
      strokeDashStyle.strokeLinecap = 'round';
      strokeDashStyle.strokeDasharray = `0, ${2 * borderWidth}`;
      break;
    case BorderStyle.Dashed:
      strokeDashStyle.strokeDasharray = `${3 * borderWidth}, ${2 * borderWidth}`;
      break;
    case BorderStyle.Solid:
    default:
      break;
  }

  return strokeDashStyle;
};

/**
 * Clips {dimension} to 0 if negative
 *
 * @param dimension dimension to normalize
 * @returns
 */
export const normalizeDimension = (dimension: number): number => {
  return Math.max(0, dimension);
};

export const generateGradientId = (id?: string) => {
  return `linearGradient-${id || nanoid()}`;
};

export const generateLinearGradient = (fillColor: string | string[], fillPercent: number, id?: string) => {
  if (fillPercent == null || !fillColor[1]) {
    return null;
  }

  const gradientId = generateGradientId(id);
  return (
    <linearGradient id={gradientId} x1={'50%'} y1={'100%'} x2={'50%'} y2={'0%'}>
      <stop offset={`${100 - fillPercent}%`} stopColor={fillColor[0]} />
      <stop offset={`${100 - fillPercent}%`} stopColor={fillColor[1]} />
    </linearGradient>
  );
};

export const generateSvgId = (id: string) => `triangle-svg-${id}`;
export const generateFillId = (id: string) => `triangle-fill-${id}`;
export const generateBorderId = (id: string) => `triangle-border-${id}`;

/**
 * Generates SVG Icon with given attributes
 *
 * @param {number} widthPx width pixel of SVG
 * @param {number} heightPx height pixel of SVG
 * @param {{string, BorderStyle, number}} border border color, border style, border width of SVG
 * @param {string} id widget Id of SVG
 * @param {string | string[]} fillColor fill color of SVG
 * @param {number | undefined} fillPercent color gradient percent of SVG
 * @returns
 */
export const generateSVGIcon = ({
  widthPx,
  heightPx,
  border,
  id,
  fillColor,
  fillPercent,
  fillPath,
  borderPath,
  isReadOnly,
  ...other
}: ShapeProps) => {
  const { width: borderWidth, style: borderStyle, color: borderColor } = border;

  const borderStyleAttributes = generateStrokeFromBorderStyle(borderStyle, borderWidth);
  const hasSecondaryColorFill =
    Array.isArray(fillColor) && fillColor[1] && fillPercent && fillPercent >= 0 && fillPercent <= 100;
  const gradientId = generateGradientId(id);

  return (
    <svg
      id={isReadOnly ? `readonly-${generateSvgId(id)}` : generateSvgId(id)}
      xmlns='http://www.w3.org/2000/svg'
      width={`${widthPx}px`}
      height={`${heightPx}px`}
      {...other}
    >
      {hasSecondaryColorFill && <defs>{generateLinearGradient(fillColor, fillPercent, id)}</defs>}

      {/* Fill Container */}
      <path
        id={isReadOnly ? `readonly-${generateFillId(id)}` : generateFillId(id)}
        fill={hasSecondaryColorFill ? `url('#${gradientId}')` : fillColor[0]}
        d={fillPath}
      />

      {/* Border */}
      <path
        id={isReadOnly ? `readonly-${generateBorderId(id)}` : generateBorderId(id)}
        d={borderPath}
        stroke={borderColor}
        strokeWidth={`${borderWidth}px`}
        fill={'none'}
        {...borderStyleAttributes}
      />
    </svg>
  );
};
